import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAppToast } from "@/hooks/useAppToast";
import { formatRecurrenceIntervalLong } from "@/lib/recurrenceLabels";
import {
  cancelSubscription,
  getMySubscriptions,
  SubscriptionMine,
} from "@/services/student/subscriptions.api";

const formatDate = (iso?: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusLabel = (status: string) => {
  switch (status) {
    case "Active":
      return "Ativa";
    case "PastDue":
      return "Pagamento pendente";
    case "Cancelled":
      return "Cancelada";
    case "Expired":
      return "Expirada";
    default:
      return status;
  }
};

export const StudentSubscriptionsPage = () => {
  const toast = useAppToast();
  const [items, setItems] = useState<SubscriptionMine[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState<SubscriptionMine | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMySubscriptions();
      setItems(data);
    } catch {
      toast.error("Não foi possível carregar suas assinaturas.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const updated = await cancelSubscription(cancelTarget.id);
      setItems((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      toast.success("Assinatura cancelada. Você mantém o acesso até o fim do período pago.");
      setCancelTarget(null);
    } catch {
      toast.error("Não foi possível cancelar. Tente novamente.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-foreground">Minha assinatura</h1>
        <p className="text-sm text-text-secondary">
          Status, próxima cobrança e cancelamento. O acesso continua até o fim do período já pago.
        </p>
      </header>

      {loading ? (
        <p className="text-sm text-text-secondary">Carregando…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-text-secondary">
          Você ainda não tem assinaturas. Pacotes avulsos aparecem em Pacotes.
        </p>
      ) : (
        <ul className="space-y-4">
          {items.map((sub) => (
            <li
              key={sub.id}
              className="rounded-xl border border bg-background p-5 flex flex-col gap-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-medium text-foreground">
                    {sub.packageName || "Pacote"}
                  </h2>
                  <p className="text-sm text-text-secondary mt-0.5">
                    {statusLabel(sub.status)} · cobrança{" "}
                    {formatRecurrenceIntervalLong(sub.intervalMonths)}
                  </p>
                </div>
                {(sub.status === "Active" || sub.status === "PastDue") && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCancelTarget(sub)}
                  >
                    Cancelar assinatura
                  </Button>
                )}
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-text-tertiary">Período atual</dt>
                  <dd className="text-foreground">
                    {formatDate(sub.currentPeriodStart)} — {formatDate(sub.currentPeriodEnd)}
                  </dd>
                </div>
                <div>
                  <dt className="text-text-tertiary">Próxima cobrança</dt>
                  <dd className="text-foreground">
                    {sub.status === "Active" ? formatDate(sub.nextBillingAt) : "—"}
                  </dd>
                </div>
                {sub.status === "PastDue" ? (
                  <div className="sm:col-span-2 rounded-lg bg-destructive/10 px-3 py-2 text-destructive">
                    Falha na renovação. Atualize o cartão no checkout ou no app Mercado Pago para
                    manter o acesso após o período de tolerância.
                  </div>
                ) : null}
              </dl>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar assinatura?</DialogTitle>
            <DialogDescription>
              As cobranças futuras serão interrompidas. Você continua com acesso até{" "}
              <strong>{formatDate(cancelTarget?.currentPeriodEnd)}</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setCancelTarget(null)}>
              Manter assinatura
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={cancelling}
              onClick={() => void handleConfirmCancel()}
            >
              {cancelling ? "Cancelando…" : "Confirmar cancelamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
